import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Spin } from "antd";
import Add from "../../assets/Add";
import Task from "./components/Task";
import {
  DeletePipelineTaskApi,
  GetTaskApi,
  ReOrderTaskApi,
} from "../../api/request/pipeline";
import toast from "react-hot-toast";
import BackButton from "../../components/button/BackButton";
import ConfirmationModal from "../../components/modals/ConfirmationModal";

const ViewPipeline = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const pipeline = state?.pipeline || {};
  const [taskData, setTaskData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState("pipeline");
  const [mode, setMode] = useState("create");
  const [task, setTask] = useState({});
  const [IsDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState([]);

  const getTaskList = async () => {
    try {
      setIsLoading(true);
      const params = { pipeline_temp_id: id };
      const { data } = await GetTaskApi(params);
      if (data.status) {
        setTaskData(data?.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (category === "pipeline") {
      setTask({});
      getTaskList();
    }
  }, [category]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const newItems = Array.from(taskData);
    const [movedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, movedItem);
    setTaskData(newItems);

    try {
      let params = {
        task_id: result?.draggableId,
        task_order: result?.destination?.index + 1,
      };
      await ReOrderTaskApi(params);
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const handleTask = (mode, data) => {
    if (data) setTask(data);
    setMode(mode);
    setCategory("task");
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const params = { pipeline_task_id: selectedTask?.id };
      const { data } = await DeletePipelineTaskApi(params);
      if (data.status) {
        toast.success(data.message);

        setTaskData((prevTasks) =>
          prevTasks?.filter((task) => task?.id !== selectedTask?.id)
        );
        setIsDeleteModalOpen(false);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      {category === "pipeline" && (
        <div className="border rounded-lg bg-White">
          <div className="p-4 sm:flex items-center justify-between border-b">
            <div className="flex items-center gap-3">
              <BackButton />
              <h2 className="title">{pipeline?.name}</h2>
            </div>

            <button
              className="btn-primary !px-4 flex items-center gap-2 sm:mt-0 mt-3 ml-auto"
              onClick={() => handleTask("add")}
            >
              <Add />
              <span>Add Task</span>
            </button>
          </div>

          <div className="p-4">
            {isLoading ? (
              <div className="flex flex-col items-center gap-2 my-16">
                <Spin />
                <p className="text-secondaryLight1">Loading...</p>
              </div>
            ) : (
              <>
                {taskData?.length ? (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {taskData?.map((item, index) => (
                            <Draggable
                              key={item.id}
                              draggableId={item.id}
                              index={index}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className="flex items-center gap-3 mb-4 sm:mb-6"
                                >
                                  <img
                                    src="/assets/icons/move-dnd.svg"
                                    className="size-6"
                                    alt="icon"
                                  />
                                  <div className="w-full flex items-center justify-between gap-6 border rounded-lg sm:p-6 p-4">
                                    <div className="flex flex-col gap-1">
                                      <h1 className="title">{item?.name}</h1>
                                      <p className="text-Gray">
                                        Time Duration :{" "}
                                        <span className="text-dark">
                                          {item?.task_duration} Hour
                                        </span>
                                      </p>
                                      <p className="text-Gray">
                                        {item?.description}
                                      </p>
                                      {item?.is_next_payment ? (
                                        <p className="text-Gray">
                                          Verify Payment :{" "}
                                          <span className="text-dark">Yes</span>
                                        </p>
                                      ) : (
                                        ""
                                      )}
                                      {item?.is_signature ? (
                                        <p className="text-Gray">
                                          Verify Signature :{" "}
                                          <span className="text-dark">Yes</span>
                                        </p>
                                      ) : (
                                        ""
                                      )}

                                      {item?.is_send_review_docs ? (
                                        <p className="text-Gray">
                                          Send Document for Client Review and
                                          Verification :{" "}
                                          <span className="text-dark">Yes</span>
                                        </p>
                                      ) : (
                                        ""
                                      )}
                                    </div>

                                    <div className="flex items-center gap-3">
                                      <button
                                        className="btn-outlined flex items-center !px-4 !py-1.5"
                                        onClick={() => handleTask("edit", item)}
                                      >
                                        <img
                                          src="/assets/icons/edit.svg"
                                          alt="icon"
                                          className="size-4 me-2"
                                        />
                                        Edit
                                      </button>
                                      <button
                                        className="btn-outlined flex items-center !px-4 !py-1.5"
                                        onClick={() => {
                                          setSelectedTask(item);
                                          setIsDeleteModalOpen(true);
                                        }}
                                      >
                                        <img
                                          src="/assets/icons/delete.svg"
                                          alt="icon"
                                          className="size-4 me-2"
                                        />
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="w-1/2 flex flex-col items-center justify-center gap-5">
                      <img
                        src="/assets/images/empty-task.png"
                        className="h-[300px] w-[300px]"
                        alt="Empty task"
                      />
                      <h1 className="text-center font-medium text-3xl mt-5">
                        You have no task
                      </h1>
                      <p className="text-center text-Gray">
                        Using this software, you can ask yourquestions and
                        receive articles using artificial intelligence assistant
                      </p>
                      <button
                        className="btn-primary !px-4 flex items-center gap-2"
                        onClick={() => handleTask("add")}
                      >
                        <Add />
                        <span>Add Task</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {category === "task" && (
        <div>
          <Task
            pipelineId={id}
            task={task}
            mode={mode}
            setCategory={setCategory}
          />
        </div>
      )}

      <ConfirmationModal
        isOpen={IsDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message="Are you sure you want to delete this service flow task?"
        onConfirm={handleDelete}
        isLoading={deleteLoading}
        loadingLabel="Deleting..."
      />
    </>
  );
};

export default ViewPipeline;
